// src/ui/components/TimeTracking.tsx
import * as React from 'react'
import {
  Box,
  Card,
  EmptyState,
  Grid,
  GridItem,
  HStack,
  NativeSelect,
  Text,
  Button,
  SegmentGroup,
  Separator,
  Tabs,
} from '@chakra-ui/react'
import { LuCalendarPlus, LuFileClock } from 'react-icons/lu'
import { parseISODate } from '@/pages/timetracking/timeWidget.utils'
import {
  weekNumberFromYearStart,
  toISODate,
  addDays,
  startOfMonth,
  endOfMonth,
} from '@/pages/timetracking/timeWidget.utils'

import CheckboxCardFrame, { type CheckboxCardOption } from '../../ui/components/CheckboxCardFrame'
import TimeSeriesLineChart from '../../ui/components/TimeSeriesLineChart'
import { ActivityPanels } from '../../ui/components/ActivityPanels'
import type { PeriodSelection, Segment } from '@/pages/timetracking/timeWidget.types'
import { useTimeWidgetData } from '@/pages/timetracking/useTimeWidgetData'
import { WidgetShell } from '@/components/widget-wrappers/WidgetShell'

import type { TimeframeKey } from '../../ui/components/utils/worktimeTransforms'
import type { ChartPercentRow, YearMonth } from '../../ui/components/types'

export type AnnualPlan = Record<string, number>

export interface TimeTrackingProps {
  registrationYear: number
  year: number
  onYearChange: (next: number) => void

  plan: AnnualPlan
  title?: string

  onCreatePlanning?: () => void
  onCreateOrder?: () => void

  recipients: CheckboxCardOption[]
  serviceTabs: string[]
}

const MONTHS = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']

function TimeTracking({
  registrationYear,
  year,
  onYearChange,
  plan,
  title = 'Arbeitszeit',
  onCreatePlanning,
  onCreateOrder,
  recipients,
  serviceTabs,
}: TimeTrackingProps) {
  // Local UI state
  const [timeframe, setTimeframe] = React.useState<TimeframeKey>('Monat')
  const [category, setCategory] = React.useState<string>('Übersicht')
  type MonthYear = { month?: number; year?: number }

  const [specificFromDraft, setSpecificFromDraft] = React.useState<MonthYear>({})
  const [specificToDraft, setSpecificToDraft] = React.useState<MonthYear>({})
  const [specificFrom, setSpecificFrom] = React.useState<MonthYear>({})
  const [specificTo, setSpecificTo] = React.useState<MonthYear>({})
  const now = new Date()
  const NOW_YEAR = now.getFullYear()
  const NOW_MONTH = now.getMonth() + 1

  const [selectedRecipients, setSelectedRecipients] = React.useState<string[]>([])
  const handleClearRecipients = () => {
    setSelectedRecipients([])
  }

  const ALL_SERIES = React.useMemo(() => ['Gesamt', 'TOJ', 'Beratung', 'Schulung', 'Pilot'], [])

  const [visibleSeries, setVisibleSeries] = React.useState<string[]>(() => ALL_SERIES)

  // Chart state
  const [data, setData] = React.useState<ChartPercentRow[]>([])
  const [ticks, setTicks] = React.useState<YearMonth[]>([])
  const [loading, setLoading] = React.useState(true)

  const period: PeriodSelection = React.useMemo(() => {
    if (timeframe === 'YTD') return { kind: 'YTD' }

    if (timeframe === 'Jahr') {
      const now = new Date()
      const fromDate = new Date(now)
      fromDate.setMonth(fromDate.getMonth() - 12)
      return { kind: 'CUSTOM', from: toISODate(fromDate), to: toISODate(now) }
    }

    if (timeframe === 'Quartal') {
      const now = new Date()
      const fromDate = new Date(now)
      fromDate.setMonth(fromDate.getMonth() - 3)

      return { kind: 'CUSTOM', from: toISODate(fromDate), to: toISODate(now) }
    }

    if (timeframe === 'Woche') {
      const now = new Date()
      const from = toISODate(addDays(now, -6))
      const to = toISODate(now)
      return { kind: 'CUSTOM', from, to }
    }

    if (timeframe === 'Monat') {
      const now = new Date()
      const fromDate = new Date(now)
      fromDate.setMonth(fromDate.getMonth() - 1)

      const from = toISODate(fromDate)
      const to = toISODate(now)

      return { kind: 'CUSTOM', from, to }
    }

    if (timeframe === 'Spezifisch') {
      const ready = specificFrom.year && specificFrom.month && specificTo.year && specificTo.month

      if (!ready) {
        return { kind: 'CUSTOM', from: toISODate(new Date()), to: toISODate(new Date()) }
      }

      const fromDate = startOfMonth(specificFrom.year!, specificFrom.month!)
      const toDate = endOfMonth(specificTo.year!, specificTo.month!)

      return { kind: 'CUSTOM', from: toISODate(fromDate), to: toISODate(toDate) }
    }

    return { kind: 'YEAR', year }
  }, [timeframe, year, specificFrom.month, specificFrom.year, specificTo.month, specificTo.year])

  const segment: Segment = React.useMemo(() => {
    if (category === 'TOJ') return 'TOJ'
    if (category === 'Beratung') return 'CONSULTING'
    if (category === 'Schulung') return 'TRAINING'
    if (category === 'Pilot') return 'PILOT'
    return 'OVERVIEW'
  }, [category])

  const tw = useTimeWidgetData({
    period,
    timeframe,
    selectedRecipientIds: selectedRecipients, // [] => all allowed
    segment, // chart only
  })

  // Async loader extracted into useCallback
  const loadData = React.useCallback(async () => {
    setLoading(true)

    const narrowed =
      category === 'Übersicht'
        ? data
        : data.map((r: any) => {
            const row: Record<string, number | null | YearMonth> = { month: r.month }

            const v = (r as Record<string, unknown>)[category]
            row[category] = typeof v === 'number' ? v : null

            const p = (r as Record<string, unknown>)['Plan']
            if (typeof p === 'number') row['Plan'] = p

            return row as any
          })

    setData(narrowed)
    setTicks(ticks)
    setLoading(false)
  }, [year, plan, category, timeframe, selectedRecipients])

  const didInitRecipients = React.useRef(false)

  React.useEffect(() => {
    if (timeframe !== 'Spezifisch') return

    const now = new Date()
    const y = now.getFullYear()
    const m = now.getMonth() + 1

    setSpecificFromDraft(v => (v.year && v.month ? v : { year: y, month: m }))
    setSpecificToDraft(v => (v.year && v.month ? v : { year: y, month: m }))
    setSpecificFrom(v => (v.year && v.month ? v : { year: y, month: m }))
    setSpecificTo(v => (v.year && v.month ? v : { year: y, month: m }))
  }, [timeframe])

  React.useEffect(() => {
    if (didInitRecipients.current) return
    if (tw.recipients.length === 0) return

    setSelectedRecipients([])
    didInitRecipients.current = true
  }, [tw.recipients])

  // Trigger load
  React.useEffect(() => {
    loadData()
  }, [loadData])

  const handleRecipientsChange = (next: string[], _meta: { id: string; checked: boolean }) => {
    setSelectedRecipients(next)
  }

  const cmpYM = (a: { year?: number; month?: number }, b: { year?: number; month?: number }) => {
    if (!a.year || !a.month || !b.year || !b.month) return 0
    if (a.year !== b.year) return a.year - b.year
    return a.month - b.month
  }

  const isYMComplete = (v: { year?: number; month?: number }) => !!v.year && !!v.month

  const specificReady =
    isYMComplete(specificFromDraft) &&
    isYMComplete(specificToDraft) &&
    cmpYM(specificToDraft, specificFromDraft) >= 0

  const handleApplySpecific = () => {
    if (!specificReady) return
    setSpecificFrom(specificFromDraft)
    setSpecificTo(specificToDraft)
  }

  const specificDirty =
    specificFromDraft.year !== specificFrom.year ||
    specificFromDraft.month !== specificFrom.month ||
    specificToDraft.year !== specificTo.year ||
    specificToDraft.month !== specificTo.month

  React.useEffect(() => {
    if (timeframe !== 'Spezifisch') return
    if (!isYMComplete(specificFromDraft)) return
    if (isYMComplete(specificToDraft)) return

    setSpecificToDraft({ ...specificFromDraft })
  }, [timeframe, specificFromDraft.year, specificFromDraft.month])

  React.useEffect(() => {
    if (timeframe !== 'Spezifisch') return

    const clamp = (v: { year?: number; month?: number }) => {
      if (!v.year || !v.month) return v
      if (v.year > NOW_YEAR) return { year: NOW_YEAR, month: NOW_MONTH }
      if (v.year === NOW_YEAR && v.month > NOW_MONTH) return { year: NOW_YEAR, month: NOW_MONTH }
      return v
    }

    setSpecificFromDraft(prev => clamp(prev))
    setSpecificToDraft(prev => clamp(prev))
    setSpecificFrom(prev => clamp(prev))
    setSpecificTo(prev => clamp(prev))
  }, [timeframe, NOW_YEAR, NOW_MONTH])

  React.useEffect(() => {
    if (timeframe !== 'Spezifisch') return

    const clampYM = (v: { year?: number; month?: number }) => {
      if (!v.year) return v
      if (!v.month) return v
      if (v.year > NOW_YEAR) return { year: NOW_YEAR, month: NOW_MONTH }

      if (v.year === NOW_YEAR && v.month > NOW_MONTH) {
        return { year: NOW_YEAR, month: NOW_MONTH }
      }
      return v
    }
    setSpecificFromDraft(prev => clampYM(prev))
    setSpecificToDraft(prev => clampYM(prev))
  }, [
    timeframe,
    NOW_YEAR,
    NOW_MONTH,
    specificFromDraft.year,
    specificFromDraft.month,
    specificToDraft.year,
    specificToDraft.month,
  ])

  React.useEffect(() => {
    if (timeframe !== 'Spezifisch') return
    if (!isYMComplete(specificFromDraft) || !isYMComplete(specificToDraft)) return
    if (cmpYM(specificToDraft, specificFromDraft) < 0) {
      setSpecificToDraft({ ...specificFromDraft })
    }
  }, [
    timeframe,
    specificFromDraft.year,
    specificFromDraft.month,
    specificToDraft.year,
    specificToDraft.month,
  ])

  React.useEffect(() => {
    if (!serviceTabs?.length) return
    if (!serviceTabs.includes(category)) {
      setCategory(serviceTabs[0]) // usually 'Übersicht'
    }
  }, [serviceTabs, category])

  const isRollingWeek = timeframe === 'Woche'

  const chartRows = React.useMemo(() => {
    const now = new Date()
    const labels = tw.chart.labels
    const keys = tw.chart.keys ?? labels // fallback if old data
    const n = labels.length || 1
    const planTOJ = tw.kpis.toj.ordered
    const planCONS = tw.kpis.consulting.ordered
    const planTR = tw.kpis.training.ordered
    const planPILOT = tw.kpis.pilot?.ordered ?? 0
    const planAll = planTOJ + planCONS + planTR + planPILOT
    const pct = (v: number, denom: number) => (denom > 0 ? (v / denom) * 100 : 0)
    const planSelected =
      segment !== 'OVERVIEW'
        ? segment === 'TOJ'
          ? planTOJ
          : segment === 'CONSULTING'
            ? planCONS
            : segment === 'TRAINING'
              ? planTR
              : planPILOT
        : planAll

    let lastPastIdx = -1

    const rollingSum = (arr: number[], window: number) =>
      arr.map((_, i) => {
        let s = 0
        for (let j = Math.max(0, i - window + 1); j <= i; j++) s += arr[j] ?? 0
        return s
      })

    const totalPlannedSelected = planSelected

    if (timeframe === 'Woche' || timeframe === 'Monat') {
      const totalPlannedAll = planAll
      const pctAll = (v: number) => (totalPlannedAll > 0 ? (v / totalPlannedAll) * 100 : 0)
      const pctSel = (v: number) =>
        totalPlannedSelected > 0 ? (v / totalPlannedSelected) * 100 : 0

      const pctBase = segment === 'OVERVIEW' ? pctAll : pctSel

      let sumTOJ = 0
      let sumCONS = 0
      let sumTR = 0
      let sumPILOT = 0

      const rollingRows = labels.map((label, i) => {
        const pTOJ = tw.chart.orderedByType.TOJ[i] ?? 0
        const pCONS = tw.chart.orderedByType.CONSULTING[i] ?? 0
        const pTR = tw.chart.orderedByType.TRAINING[i] ?? 0

        sumTOJ += tw.chart.workedByType.TOJ[i] ?? 0
        sumCONS += tw.chart.workedByType.CONSULTING[i] ?? 0
        sumTR += tw.chart.workedByType.TRAINING[i] ?? 0
        sumPILOT += tw.chart.workedByType.PILOT[i] ?? 0
        const progress = ((i + 1) / labels.length) * 100

        const row: Record<string, number | string | null> = {
          month: label,
          Plan: progress, // always ends at 100%
        }

        if (segment === 'OVERVIEW') {
          row['Gesamt'] = pctAll(sumTOJ + sumCONS + sumTR + sumPILOT)
          row['TOJ'] = pctAll(sumTOJ)
          row['Beratung'] = pctAll(sumCONS)
          row['Schulung'] = pctAll(sumTR)
          row['Pilot'] = pctAll(sumPILOT)
        } else if (segment === 'TOJ') {
          row['TOJ'] = pctSel(sumTOJ)
        } else if (segment === 'CONSULTING') {
          row['Beratung'] = pctSel(sumCONS)
        } else if (segment === 'TRAINING') {
          row['Schulung'] = pctSel(sumTR)
        } else if (segment === 'PILOT') {
          row['Pilot'] = pctSel(sumPILOT)
        }

        return row as any
      })

      rollingRows.unshift({
        month: '__START__',
        Plan: 0,
        ...(segment === 'OVERVIEW'
          ? { Gesamt: 0, TOJ: 0, Beratung: 0, Schulung: 0, Pilot: 0 }
          : segment === 'TOJ'
            ? { TOJ: 0 }
            : segment === 'CONSULTING'
              ? { Beratung: 0 }
              : segment === 'TRAINING'
                ? { Schulung: 0 }
                : { Pilot: 0 }),
      } as any)

      return rollingRows
    }

    const isDateKey = keys.some(
      k => typeof k === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(String(k))
    )

    if (isDateKey) {
      const nowKey = toISODate(now) // e.g. "2026-02-18"
      for (let i = 0; i < keys.length; i++) {
        if (String(keys[i]) <= nowKey) lastPastIdx = i
      }
    }

    const isKW =
      keys.some(k => typeof k === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(String(k))) &&
      labels.some(l => String(l).startsWith('KW-'))

    const isQuarter = keys.some(k => typeof k === 'string' && /^\d{4}-Q[1-4]$/.test(String(k)))
    const isMonth = keys.some(k => typeof k === 'string' && /^\d{4}-\d{2}$/.test(String(k)))
    const isYear = keys.some(k => typeof k === 'string' && /^\d{4}$/.test(String(k)))

    if (isKW) {
      // If user selected a different year than "now", handle it cleanly
      const selectedYear = year // <- you already have `year` prop in TimeTracking
      const nowYear = now.getFullYear()

      if (selectedYear < nowYear) {
        // whole selected year is in the past => no future buckets
        lastPastIdx = labels.length - 1
      } else if (selectedYear > nowYear) {
        // whole selected year is in the future => everything future
        lastPastIdx = -1
      } else {
        // selected year is current year => compare KW numbers
        const mondayOfWeek = (d: Date) => {
          const tmp = new Date(d.getFullYear(), d.getMonth(), d.getDate())
          const weekday = (tmp.getDay() + 6) % 7 // Mon=0 ... Sun=6
          tmp.setDate(tmp.getDate() - weekday)
          tmp.setHours(0, 0, 0, 0)
          return tmp
        }

        // Use the SAME rule you used for KW labels (weekNumberFromYearStart)
        const currentKW = weekNumberFromYearStart(mondayOfWeek(now), selectedYear)

        for (let i = 0; i < labels.length; i++) {
          const kw = Number(String(labels[i]).slice(3)) // "KW-12" -> 12
          if (!Number.isNaN(kw) && kw <= currentKW) lastPastIdx = i
        }
      }
    } else if (isQuarter) {
      const selectedYear = year
      const nowYear = now.getFullYear()

      if (selectedYear < nowYear) {
        lastPastIdx = keys.length - 1
      } else if (selectedYear > nowYear) {
        lastPastIdx = -1
      } else {
        const currentQ = Math.floor(now.getMonth() / 3) + 1 // 1..4
        for (let i = 0; i < keys.length; i++) {
          const m = String(keys[i]).match(/^(\d{4})-Q([1-4])$/)
          if (!m) continue
          const q = Number(m[2])
          if (q <= currentQ) lastPastIdx = i
        }
      }
    } else if (isMonth) {
      const nowKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
      for (let i = 0; i < keys.length; i++) {
        if (String(keys[i]) <= nowKey) lastPastIdx = i
      }
    } else {
      // fallback: don’t cut off
      lastPastIdx = keys.length - 1
    }

    let sumTOJ = 0
    let sumCONS = 0
    let sumTR = 0

    let sumPlanSel = 0
    const pctSel = (v: number) => (totalPlannedSelected > 0 ? (v / totalPlannedSelected) * 100 : 0)

    const rows = labels.map((label, i) => {
      const isFuture = i > lastPastIdx

      const pTOJ = tw.chart.orderedByType.TOJ[i] ?? 0
      const pCONS = tw.chart.orderedByType.CONSULTING[i] ?? 0
      const pTR = tw.chart.orderedByType.TRAINING[i] ?? 0

      sumPlanSel +=
        segment === 'TOJ'
          ? pTOJ
          : segment === 'CONSULTING'
            ? pCONS
            : segment === 'TRAINING'
              ? pTR
              : pTOJ + pCONS + pTR

      // Always advance cum based on real arrays (doesn’t matter for future, we null it)
      sumTOJ += tw.chart.workedByType.TOJ[i] ?? 0
      sumCONS += tw.chart.workedByType.CONSULTING[i] ?? 0
      sumTR += tw.chart.workedByType.TRAINING[i] ?? 0
      const sumAll = sumTOJ + sumCONS + sumTR
      const denom = (lastPastIdx >= 0 ? lastPastIdx + 1 : labels.length) || 1
      const progress = ((i + 1) / denom) * 100

      const row: Record<string, number | string | null> = {
        month: label,
        Plan: isFuture ? null : Math.min(100, progress),
      }

      if (segment === 'OVERVIEW') {
        row['Gesamt'] = isFuture ? null : pct(sumAll, planAll)
        row['TOJ'] = isFuture ? null : pct(sumTOJ, planAll)
        row['Beratung'] = isFuture ? null : pct(sumCONS, planAll)
        row['Schulung'] = isFuture ? null : pct(sumTR, planAll)
      } else if (segment === 'TOJ') {
        row['TOJ'] = isFuture ? null : pctSel(sumTOJ)
      } else if (segment === 'CONSULTING') {
        row['Beratung'] = isFuture ? null : pctSel(sumCONS)
      } else if (segment === 'TRAINING') {
        row['Schulung'] = isFuture ? null : pctSel(sumTR)
      }

      return row as any
    })
    rows.unshift({
      month: '__START__',
      Plan: 0,
      ...(segment === 'OVERVIEW'
        ? { Gesamt: 0, TOJ: 0, Beratung: 0, Schulung: 0 }
        : segment === 'TOJ'
          ? { TOJ: 0 }
          : segment === 'CONSULTING'
            ? { Beratung: 0 }
            : { Schulung: 0 }),
    } as any)

    if (lastPastIdx >= 0) {
      return rows.slice(0, lastPastIdx + 2)
    }
    return rows
  }, [
    tw.chart.keys,
    tw.chart.labels,
    tw.chart.workedByType,
    tw.chart.orderedByType,
    tw.kpis,
    segment,
    year,
  ])

  const noRecipientSelected = selectedRecipients.length === 0

  const panelItems = React.useMemo(() => {
    const k = tw.kpis
    return [
      { type: 'TOJ', used: k.toj.worked, planned: k.toj.ordered, unit: k.toj.unit, tip: '' },
      {
        type: 'Beratung',
        used: k.consulting.worked,
        planned: k.consulting.ordered,
        unit: k.consulting.unit,
        tip: '',
      },
      {
        type: 'Schulung',
        used: k.training.worked,
        planned: k.training.ordered,
        unit: k.training.unit,
        tip: '',
      },
      {
        type: 'Pilot',
        used: k.pilot?.worked ?? 0,
        planned: k.pilot?.ordered ?? 0,
        unit: k.pilot?.unit ?? 'h',
        tip: '',
      },
    ]
  }, [tw.kpis])

  const isHoursMode = timeframe === 'Woche' || timeframe === 'Monat'

  return (
    <WidgetShell
      data-testid="time-tracking-widget"
      minW="350px"
      minH={{ base: '650px', md: '720px' }}
      title={title}
      headerRight={
        <HStack gap={2} wrap="wrap">
          {onCreateOrder && (
            <Button variant="ghost" colorPalette="gray" onClick={onCreateOrder}>
              <LuFileClock />
              Neue Bestellung
            </Button>
          )}
          {onCreatePlanning && (
            <Button variant="ghost" colorPalette="gray" onClick={onCreatePlanning}>
              <LuCalendarPlus />
              Aktivität hinzufügen
            </Button>
          )}
        </HStack>
      }
    >
      {/* Timeframe */}
      <Tabs.Root
        value={timeframe}
        onValueChange={e => {
          if (e.value) setTimeframe(e.value as TimeframeKey)
        }}
        variant="subtle"
        colorPalette="gray"
      >
        <Tabs.List>
          <Tabs.Indicator />
          <Tabs.Trigger value="YTD">YTD</Tabs.Trigger>
          <Tabs.Trigger value="Jahr">Jahr</Tabs.Trigger>
          <Tabs.Trigger value="Quartal">Quartal</Tabs.Trigger>
          <Tabs.Trigger value="Monat">Monat</Tabs.Trigger>
          <Tabs.Trigger value="Woche">Woche</Tabs.Trigger>
          <Tabs.Trigger value="Spezifisch">Spezifisch</Tabs.Trigger>
        </Tabs.List>
      </Tabs.Root>
      {timeframe === 'Spezifisch' && (
        <Box pt={3} pb={3}>
          <Grid templateColumns={{ base: '1fr', md: '1fr 1fr auto' }} gap={6} alignItems="end">
            {/* FROM */}
            <GridItem>
              <Text>Von</Text>
              <HStack>
                <NativeSelect.Root>
                  <NativeSelect.Field
                    value={specificFromDraft.month ?? ''}
                    onChange={e =>
                      setSpecificFromDraft(v => ({
                        ...v,
                        month: Number(e.target.value) || undefined,
                      }))
                    }
                  >
                    <option value="">Monat</option>
                    {MONTHS.map((m, idx) => {
                      const month = idx + 1
                      const y = specificFromDraft.year
                      const isFuture =
                        (y == null && month > NOW_MONTH) || (y === NOW_YEAR && month > NOW_MONTH)

                      return (
                        <option key={m} value={month} disabled={isFuture}>
                          {m}
                        </option>
                      )
                    })}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
                <NativeSelect.Root>
                  <NativeSelect.Field
                    value={specificFromDraft.year ?? ''}
                    onChange={e =>
                      setSpecificFromDraft(v => ({
                        ...v,
                        year: Number(e.target.value) || undefined,
                      }))
                    }
                  >
                    <option value="">Jahr</option>
                    {Array.from({ length: 3 }, (_, i) => NOW_YEAR - i).map(y => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </HStack>
            </GridItem>

            {/* TO */}
            <GridItem>
              <Text>Bis</Text>
              <HStack>
                <NativeSelect.Root>
                  <NativeSelect.Field
                    value={specificToDraft.month ?? ''}
                    onChange={e =>
                      setSpecificToDraft(v => ({
                        ...v,
                        month: Number(e.target.value) || undefined,
                      }))
                    }
                  >
                    <option value="">Monat</option>
                    {MONTHS.map((m, idx) => {
                      const month = idx + 1

                      const ty = specificToDraft.year
                      const fy = specificFromDraft.year
                      const fm = specificFromDraft.month

                      const isFuture =
                        (ty == null && month > NOW_MONTH) || (ty === NOW_YEAR && month > NOW_MONTH)

                      const isBeforeFrom =
                        isYMComplete(specificFromDraft) &&
                        ty != null &&
                        fy != null &&
                        (ty < fy || (ty === fy && fm != null && month < fm))

                      return (
                        <option key={m} value={month} disabled={isFuture || isBeforeFrom}>
                          {m}
                        </option>
                      )
                    })}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>

                <NativeSelect.Root>
                  <NativeSelect.Field
                    value={specificToDraft.year ?? ''}
                    onChange={e =>
                      setSpecificToDraft(v => ({
                        ...v,
                        year: Number(e.target.value) || undefined,
                      }))
                    }
                  >
                    <option value="">Jahr</option>
                    {Array.from({ length: 3 }, (_, i) => NOW_YEAR - i).map(y => {
                      const fy = specificFromDraft.year
                      const disable = fy != null && y < fy

                      return (
                        <option key={y} value={y} disabled={disable}>
                          {y}
                        </option>
                      )
                    })}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </HStack>
            </GridItem>
            <GridItem>
              <Button
                colorPalette="gray"
                variant="solid"
                onClick={handleApplySpecific}
                disabled={!specificReady || !specificDirty}
              >
                Anwenden
              </Button>
            </GridItem>
          </Grid>
        </Box>
      )}

      {/* Recipients */}
      <Box mt={1} pt={2} pb={3}>
        <CheckboxCardFrame
          options={tw.recipients.map(r => ({ id: r.id, label: r.name }))}
          value={selectedRecipients}
          onChange={(next, meta) => handleRecipientsChange(next, meta)}
        />
      </Box>

      <Separator borderColor="border" minH={5} />

      {/* 2-Column Layout */}
      <Grid
        templateColumns={{ base: '1fr', md: '1fr 1fr' }}
        gap="section"
        alignItems="stretch"
      >
        {/* LEFT COLUMN */}
        <GridItem>
          <Card.Root h="full" display="flex" flexDirection="column" minH={0}>
            <Card.Body display="flex" flexDirection="column" minH={0}>
              <SegmentGroup.Root
                value={category}
                onValueChange={e => setCategory(e.value ?? 'Übersicht')}
                colorPalette="gray"
                alignSelf="flex-start"
              >
                <SegmentGroup.Indicator />
                <SegmentGroup.Items items={serviceTabs} />
              </SegmentGroup.Root>

              {/* The graph fills whatever height the row gives the card. */}
              <Box
                flex="1"
                minH={{ base: '380px', md: '424px' }}
                display="flex"
                flexDirection="column"
              >
                {tw.emptyState?.message ? (
                  <EmptyState.Root flex="1">
                    <EmptyState.Content>
                      <EmptyState.Description whiteSpace="pre-line">
                        {tw.emptyState?.message}
                      </EmptyState.Description>
                    </EmptyState.Content>
                  </EmptyState.Root>
                ) : (
                  <TimeSeriesLineChart
                    title={title}
                    height={380}
                    data={chartRows as any}
                    isLoading={tw.loading}
                    ticks={chartRows.map(r => r.month) as any}
                    key={`${timeframe}-${segment}-${selectedRecipients.join(',')}`}
                    xKey="month"
                    yAxisPercent={true}
                  />
                )}
              </Box>
            </Card.Body>
          </Card.Root>
        </GridItem>

        {/* RIGHT COLUMN */}
        <GridItem>
          <Card.Root
            h="full"
            minH={{ base: '380px', md: '460px' }}
            display="flex"
            flexDirection="column"
          >
            <Card.Body h="100%">
              <ActivityPanels
                items={panelItems}
                title="Alle Leistungen"
                height={260}
                showTotal
                noRecipientSelected={selectedRecipients.length === 0}
              />
            </Card.Body>
          </Card.Root>
        </GridItem>
      </Grid>
    </WidgetShell>
  )
}
export default React.memo(TimeTracking)
